import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { HeartHandshake, X } from "lucide-react";
import { onMenu, isMenuOpen } from "@/lib/menuBus";

const COUNT_KEY = "dh_pageviews";
const EVERY = 10;

// Gentle Support Us nudge: shown once every 10 page loads. Never on
// admin, auth, support or reading routes; dismiss closes until the next multiple.
const isSuppressed = (path) =>
  path.startsWith("/admin") || path.startsWith("/support") || path.startsWith("/panchang") ||
  path === "/login" || path === "/register" || path === "/onboarding";

export default function SupportNudge() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { pick } = useLang();
  const { donations_enabled } = useSettings();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isSuppressed(pathname)) return;
    const n = (parseInt(sessionStorage.getItem(COUNT_KEY) || "0", 10) || 0) + 1;
    sessionStorage.setItem(COUNT_KEY, String(n));
    if (n % EVERY === 0) {
      const t = setTimeout(() => { if (!isMenuOpen()) setOpen(true); }, 8000);
      return () => clearTimeout(t);
    }
  }, [pathname]);

  // Rule 1: close (and never open over) the main menu.
  useEffect(() => onMenu((o) => { if (o) setOpen(false); }), []);

  if (!open) return null;

  const close = () => setOpen(false);
  const go = () => { close(); navigate("/support"); };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center p-4 sm:items-center" data-testid="support-nudge">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-md border border-[var(--gold-muted)] bg-surface p-7 shadow-2xl">
        <button onClick={close} data-testid="support-nudge-close" className="absolute right-4 top-4 text-ink-3 transition-colors hover:text-ink"><X className="h-5 w-5" /></button>
        <span className="flex h-12 w-12 items-center justify-center border border-[var(--gold-muted)] text-saffron">
          <HeartHandshake className="h-6 w-6" />
        </span>
        <h3 className="mt-5 font-serif-latin text-2xl text-ink">{pick("धर्मार्थ को सहयोग दें", "Support Dharmartha")}</h3>
        <p className="mt-2 text-[15px] leading-relaxed text-ink-2">
          {pick(
            "यह ज्ञानकोश श्रद्धापूर्वक बनाया गया है और विज्ञापन-रहित रखा गया है। आपका छोटा-सा सहयोग इसे जीवित रखता है और नई सामग्री जोड़ने में सहायता करता है।",
            "This knowledge library is lovingly maintained and kept ad-free. A small contribution keeps it alive and helps us add more."
          )}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={go} data-testid="support-nudge-cta" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
            <HeartHandshake className="h-4 w-4" /> {donations_enabled ? pick("अभी सहयोग करें", "Contribute now") : pick("और जानें", "Learn how to help")}
          </button>
          <button onClick={close} data-testid="support-nudge-later" className="border border-line px-5 py-2.5 text-sm text-ink-2 transition-colors hover:border-ink hover:text-ink">
            {pick("बाद में", "Maybe later")}
          </button>
        </div>
      </div>
    </div>
  );
}
